const FormConstants = require('./formConstants');
const RadioOptions = require('./radioOptionsUtils');
const PrefsDomain = require('domain/preferences');
const UseFormUtils = require('hooks/useFormPrimitives/core');
const UseFormConfigUtils = require('hooks/useFormPrimitives/config');

const BadgeFieldConfig = require('./badgeFieldConfig');
const SignupProgressHelpers = require('./signupProgressHelpers');
const PreferencesProgressHelpers = require('./preferencesProgressHelpers');
const ProfileProgressHelpers = require('./profileProgressHelpers');
const FormConfigHelpers = require('./formConfigHelpers');
const { getOriginalImageUrl } = require('utils/image');

const {
    MAX_BIO,
    MAX_CAREER,
    MAX_PRONOUNS_LENGTH,
    MAX_ETHNICITY_LENGTH,
    MAX_FIRST_AND_LAST_NAME_LENGTH
} = FormConstants;
const PRONOUN_OPTION_VALUES = RadioOptions.pronounRadioOptions.map((option) => option.value);
const internals = {};

internals.TEXT_ALERT_PREFERENCE_FIELDS = ['sms', 'phone', 'phoneCountry'];
internals.getPreferenceFieldsToShow = (props = {}) => Array.isArray(props.preferencesFieldsToShow)
    ? props.preferencesFieldsToShow
    : null;
internals.shouldShowTextAlertPreference = ({ props = {} } = {}) => {

    if (!props.includePreferences) {
        return false;
    }

    const fieldsToShow = internals.getPreferenceFieldsToShow(props);

    return !fieldsToShow || fieldsToShow.includes('sms');
};
internals.shouldShowRegularPreferences = ({ props = {} } = {}) => {

    if (!props.includePreferences) {
        return false;
    }

    const fieldsToShow = internals.getPreferenceFieldsToShow(props);

    return !fieldsToShow || fieldsToShow.some((fieldName) => !internals.TEXT_ALERT_PREFERENCE_FIELDS.includes(fieldName));
};

internals.withDefaultFieldGroupComponent = (group = {}) => {

    const {
        component,
        type,
        ...groupConfig
    } = group;

    return {
        type: type || component || 'FieldGroup',
        ...groupConfig
    };
};

internals.normalizeInterestIds = (value = []) => [].concat(value || []).map((interest) => {

    if (interest && typeof interest === 'object') {
        return interest.id;
    }

    return interest;
}).filter((interestId) => interestId !== null && typeof interestId !== 'undefined');

internals.getFormFieldValue = ({
    formMethods,
    fieldName
}) => formMethods?.getValues?.(fieldName);

internals.normalizeTransferSelectedValueForSubmit = (selectedValue) => {

    const numericValue = Number(selectedValue);

    if (selectedValue !== null
        && selectedValue !== ''
        && Number.isInteger(numericValue)
    ) {
        return {
            selectValue: numericValue,
            specifyValue: null
        };
    }

    return {
        selectValue: null,
        specifyValue: selectedValue ? String(selectedValue) : null
    };
};

internals.getIntroExtroLevelManagedDefault = ({
    signupDetails = {},
    defaults = {}
} = {}) => {

    if (Object.prototype.hasOwnProperty.call(signupDetails, 'introExtroLevel')) {
        return signupDetails.introExtroLevel;
    }

    return defaults.introExtroLevel;
};

internals.transferFieldConfig = (field = {}) => ({
    name: 'transfer',
    label: 'Are you a transfer student?',
    type: 'SelectWithSpecifyField',
    enableSwitch: true,
    labels: {
        select: 'Transfer school',
        specify: 'Transfer school name'
    },
    defaultOption: {
        hidden: true
    },
    specifyOption: {
        label: 'Other (Specify Below)',
        placeholderValue: 'Other (Specify Below)'
    },
    messages: {
        required: 'Must choose or specify transfer',
        specifyRequired: 'Transfer school name is required'
    },
    optionsSource: 'transfers',
    normalizeSelectedValueForSubmit: internals.normalizeTransferSelectedValueForSubmit,
    ...field
});

internals.handlePreferencesChange = ({
    field,
    formProps = {},
    value
} = {}) => {

    const progressAction = PreferencesProgressHelpers.buildPreferencesProgressAction({
        field,
        value,
        preferenceProfileFields: PrefsDomain.profileFields
    });

    if (progressAction.type === 'profile') {
        formProps.saveProfileProgress?.(ProfileProgressHelpers.buildProfileProgressPayload(progressAction.payload));
        return;
    }

    if (progressAction.type === 'preferences') {
        formProps.savePreferencesProgress?.(progressAction.payload);
    }
};

const FormConfig = module.exports = UseFormConfigUtils.buildForm(({ ref }) => ({
    id: 'user-inputs',
    conditionTypes: UseFormUtils.conditionTypes,
    componentRegistry: {},
    decorators: {
        groups: [
            internals.withDefaultFieldGroupComponent
        ],
        fields: [
            SignupProgressHelpers.withOnChangeProgress
        ]
    },
    shouldSyncStateDefaultsAfterPropsChange: ({
        props,
        prevProps
    }) => {
        const initialStateChanged = props.initialState !== prevProps.initialState;

        if (props.mode === 'signup') {
            return initialStateChanged;
        }

        return initialStateChanged
            || ((!!props.preferencesLoading !== !!prevProps.preferencesLoading) && !props.preferencesLoading);
    },
    uiStateDefaults: {
        croppedPicture: undefined,
        originalPicture: undefined,
        cropping: null,
        preferences: {},
        successRedirectPath: '/app/welcome',
        picSpeedDialOpen: false,
        uploadingProfilePic: false,
        convertingProfilePic: false
    },
    buildInitialManagedState: ({
        initialState,
        signupDetails = {}
    } = {}) => initialState || {
            ...FormConfig.uiStateDefaults,
            croppedPicture: signupDetails.croppedPicture,
            originalPicture: signupDetails.picture,
            preferences: signupDetails.preferences || {}
        },
    buildPreferencesProgressAction: PreferencesProgressHelpers.buildPreferencesProgressAction,
    groups: [{
        name: 'profilePhoto',
        type: 'ProfilePhotoGroup',
        fields: [
            {
                name: 'picFile',
                validateWith: {
                    type: 'profilePhoto',
                    label: 'Profile pic'
                },
                type: 'FileField',
                default: null,
                skipDefaultValidation: true,
                validateOnSubmit: true,
                uiStateDefaults: {
                    picFileErrorShow: null
                },
                trackProfileProgress: false,
                render: false
            },
            {
                name: 'picPreviewURL',
                type: 'TextField',
                default: '',
                getManagedDefault: ({
                    signupDetails,
                    defaults
                }) => getOriginalImageUrl(
                    signupDetails.croppedPicture || signupDetails.picture
                )
                    || defaults.picPreviewURL,
                trackProfileProgress: false,
                render: false
            },
            {
                name: 'cropPosition',
                type: 'object',
                default: { x: .5, y: .5 },
                trackProfileProgress: false,
                render: false
            },
            {
                name: 'cropRotation',
                type: 'number',
                default: 0,
                trackProfileProgress: false,
                render: false
            },
            {
                name: 'cropScale',
                type: 'number',
                default: 1,
                trackProfileProgress: false,
                render: false
            }
        ]
    },
    {
        name: 'identity',
        columns: 2,
        fields: [
            {
                name: 'firstName',
                label: 'First name',
                type: 'TextField',
                default: '',
                required: true,
                errorShowDefault: false,
                validateWith: 'profanity',
                normalizeValue: UseFormUtils.stripNumericCharacters,
                maxLength: MAX_FIRST_AND_LAST_NAME_LENGTH
            },
            {
                name: 'lastName',
                label: 'Last name',
                type: 'TextField',
                default: '',
                required: true,
                errorShowDefault: false,
                validateWith: 'profanity',
                normalizeValue: UseFormUtils.stripNumericCharacters,
                maxLength: MAX_FIRST_AND_LAST_NAME_LENGTH
            },
            {
                name: 'birthdate',
                label: 'Age (not shown in your profile)',
                type: 'AgeField',
                default: null,
                required: true,
                errorShowDefault: false,
                validateWith: 'ageRange',
                skipDefaultValidation: true,
                visibleWhen: {
                    all: [
                        'signupOnly',
                        { permission: 'canEditAge' }
                    ]
                }
            },
            {
                name: 'majorId',
                label: 'Major or Program',
                type: 'AutocompleteField',
                textFieldName: 'majorText',
                default: null,
                errorShowDefault: false,
                validateWith: {
                    type: 'namedSelection',
                    textFieldName: 'majorText',
                    selectedFieldName: 'majorId',
                    permission: 'canEditMajor',
                    message: 'Please choose a major in the list'
                },
                progressFieldName: 'major',
                buildSignupProgressPayload: ({ value }) => ({
                    majorId: value ? [value] : []
                }),
                refName: 'majorsTextfield',
                optionsSource: 'majors',
                visibleWhen: { permission: 'canEditMajor' }
            },
            {
                name: 'hometown',
                label: 'Hometown',
                type: 'PlaceField',
                validateWith: {
                    type: 'namedSelection',
                    textFieldName: 'hometownText',
                    selectedFieldName: 'hometown',
                    selectedPath: 'name',
                    permission: 'canEditHomeTown'
                },
                orgLikeLabel: 'Where I Grew Up',
                default: {
                    id: null,
                    placeId: null,
                    name: null,
                    onCampus: null
                },
                includeOnCampus: true,
                defaultOnCampus: null,
                placeholder: 'Type to find your city/town...',
                textFieldName: 'hometownText',
                visibleWhen: { permission: 'canEditHomeTown' }
            },
            {
                name: 'hometownText',
                label: 'Hometown',
                type: 'TextField',
                default: ref('hometown', 'name'),
                uiStateDefaults: {
                    hometownTextErrorShow: false
                },
                getManagedDefault: ({
                    signupDetails,
                    defaults
                }) => signupDetails.hometown?.name || defaults.hometownText,
                onChangeEffects: {
                    when: 'clear',
                    updates: ({ refs }) => ({
                        hometown: refs.default('hometown'),
                        hometownText: refs.default('hometownText')
                    })
                },
                render: false
            },
            {
                name: 'career',
                label: 'Career aspiration',
                type: 'TextField',
                default: '',
                clearable: true,
                errorShowDefault: false,
                validateWith: 'profanity',
                maxLength: MAX_CAREER,
                visibleWhen: { permission: 'canEditCareerAspiration' }
            },
            {
                name: 'incomingClass',
                label: 'Incoming class',
                type: 'SelectField',
                default: '',
                required: true,
                errorShowDefault: false,
                validateWith: {
                    type: 'requiredWhenOptions',
                    optionsSource: 'incomingClassList',
                    permission: 'canEditIncomingClass'
                },
                skipDefaultValidation: true,
                hideErrorOnChange: true,
                optionsSource: 'incomingClassList',
                visibleWhen: { permission: 'canEditIncomingClass' }
            },
            {
                name: 'graduatingClass',
                label: 'Graduating class',
                type: 'SelectField',
                default: '',
                optionsSource: 'graduatingClassList',
                visibleWhen: { permission: 'canEditGraduatingClass' }
            },
            {
                name: 'bio',
                label: 'Bio',
                type: 'TextareaField',
                validateWith: 'profanity',
                default: '',
                clearable: true,
                placeholder: ({ formProps }) => formProps.fieldsPlaceholders?.bio || null,
                placeholderInLabel: true,
                textFieldProps: {
                    singleLine: false
                },
                maxLength: MAX_BIO,
                gridColumn: {
                    xs: '1 / -1',
                    sm: '1 / -1'
                },
                visibleWhen: { permission: 'canEditBio' }
            },
            {
                name: 'departmentId',
                label: 'Department',
                type: 'SelectField',
                default: '',
                progressFieldName: 'department',
                optionsSource: 'departments',
                visibleWhen: { permission: 'canEditDepartment' }
            },
            {
                name: 'officeId',
                label: 'Office',
                type: 'SelectField',
                default: '',
                optionsSource: 'officesList',
                visibleWhen: { permission: 'canEditOffice' }
            },
            {
                name: 'yearHiredId',
                label: 'Year hired',
                type: 'SelectField',
                default: '',
                optionsSource: 'yearsHiredList',
                visibleWhen: { permission: 'canEditYearHired' }
            },
            {
                name: 'profession',
                label: 'Profession',
                type: 'TextField',
                default: '',
                errorShowDefault: false,
                validateWith: 'profanity',
                maxLength: MAX_CAREER,
                visibleWhen: { permission: 'canEditProfession' }
            },
            {
                name: 'type',
                label: 'Badge',
                type: 'SelectField',
                default: '',
                hideWhenNoOptions: true,
                optionsSource: 'badges',
                renderOption: BadgeFieldConfig.renderOption,
                renderValue: BadgeFieldConfig.renderValue,
                textFieldProps: {
                    fieldStyles: BadgeFieldConfig.fieldStyles
                },
                visibleWhen: { permission: 'canEditBadge' }
            },
            {
                name: 'title',
                label: 'Title',
                type: 'TextField',
                validateWith: 'profanity',
                default: '',
                visibleWhen: { permission: 'canEditTitle' }
            },
            {
                name: 'majorText',
                type: 'TextField',
                default: '',
                getManagedDefault: ({
                    signupDetails,
                    defaults,
                    props
                }) => signupDetails.majorId
                    ? props.majors?.find((major) => major.id === signupDetails.majorId)?.name || defaults.majorText
                    : defaults.majorText,
                render: false
            }
        ]
    },
    {
        name: 'housingLocation',
        visibleWhen: { permission: 'canEditLiveOnCampus' },
        columns: 1,
        rowGap: 1,
        fields: [
            {
                name: 'housing',
                validateWith: {
                    type: 'namedSelection',
                    textFieldName: 'housingText',
                    selectedFieldName: 'housing',
                    selectedPath: 'name',
                    permission: 'canEditLiveOnCampus',
                    runRefValidation: true
                },
                label: 'Housing',
                type: 'CampusPlaceField',
                default: {
                    id: null,
                    placeId: null,
                    name: null,
                    onCampus: false
                },
                textFieldName: 'housingText',
                offCampusOptionsSource: 'offCampusHousingList',
                onCampusOptionsSource: 'onCampusHousingList',
                orgLikeLabel: 'Where I Live',
                offCampusLabel: 'Off-campus location:',
                onCampusLabel: 'On-campus location:',
                switchId: 'campus-toggle',
                switchLabel: 'Do you live on campus?',
                switchOffLabel: 'No/Skip',
                switchOnLabel: 'Yes',
                placeholder: 'Type to search...',
                listChoiceErrorMessage: 'Please choose an item in the list',
                gridColumn: {
                    xs: '1 / -1',
                    sm: '1 / -1'
                },
                gridSx: {
                    pb: 1
                },
                onChangeEffects: {
                    when: ({ value }) => value?.name === null,
                    updates: {
                        housingText: ''
                    }
                },
                visibleWhen: { permission: 'canEditLiveOnCampus' }
            }
        ]
    },
    {
        name: 'introextro',
        visibleWhen: { permission: 'canEditIntroExtro' },
        columns: 1,
        rowGap: 1,
        fields: [
            {
                name: 'introExtroLevel',
                label: 'Introvert / extrovert level',
                type: 'SliderWithOptOutField',
                default: null,
                required: true,
                title: 'Your Personality Trait',
                optOutValue: 'null',
                optOutLabel: 'Skip',
                min: 1,
                max: 5,
                step: 1,
                centerValue: 3,
                captions: [
                    'Very Introverted',
                    'Ambivert',
                    'Very Extroverted'
                ],
                getManagedDefault: internals.getIntroExtroLevelManagedDefault,
                validateWith: {
                    type: 'required',
                    permission: 'canEditIntroExtro',
                    message: 'Please select your personality level, or choose Skip'
                },
                options: [
                    { value: 1, label: 'Very Introverted' },
                    { value: 2, label: 'Somewhat Introverted' },
                    { value: 3, label: 'Ambivert' },
                    { value: 4, label: 'Somewhat Extroverted' },
                    { value: 5, label: 'Very Extroverted' }
                ],
                visibleWhen: { permission: 'canEditIntroExtro' }
            }
        ]
    },
    {
        name: 'preferences',
        type: 'PreferencesGroup',
        componentProps: {
            onPreferencesChange: internals.handlePreferencesChange
        },
        refName: 'userPreferences',
        visibleWhen: internals.shouldShowRegularPreferences,
        fields: [
            {
                name: 'preferences',
                type: 'object',
                default: {},
                render: false
            }
        ]
    },
    {
        name: 'bools',
        visibleWhen: UseFormUtils.visibleWhenAnyChildVisible(),
        columns: 1,
        rowGap: 0,
        fields: [
            {
                name: 'textAlertsPreference',
                type: 'TextAlertsPreferenceField',
                default: {},
                fullWidth: true,
                skipSubmitValidation: true,
                trackProfileProgress: false,
                onPreferencesChange: internals.handlePreferencesChange,
                visibleWhen: internals.shouldShowTextAlertPreference
            },
            internals.transferFieldConfig({
                default: {
                    id: null,
                    name: null
                },
                refName: 'specifyTransferTextfield',
                getManagedDefault: ({
                    signupDetails,
                    refs
                }) => signupDetails.transfer ? {
                    id: signupDetails.transfer.id,
                    name: signupDetails.transfer.id ? null : signupDetails.transfer.name
                } : refs.default('transfer'), // self-ref!
                visibleWhen: { permission: 'canEditTransfer' }
            }),
            {
                name: 'veteran',
                label: 'Are you a veteran?',
                type: 'SwitchField',
                default: false,
                getManagedDefault: ({
                    signupDetails,
                    defaults
                }) => UseFormUtils.applyBooleanDefault(signupDetails.veteran, defaults.veteran),
                visibleWhen: { permission: 'canEditVeteran' }
            },
            {
                name: 'workRemote',
                label: 'Works remotely',
                type: 'SwitchField',
                default: false,
                getManagedDefault: ({
                    signupDetails,
                    defaults
                }) => UseFormUtils.applyBooleanDefault(signupDetails.workRemote, defaults.workRemote),
                visibleWhen: { permission: 'canEditWorkRemote' }
            },
            {
                name: 'onlineStudent',
                label: 'Are you an online student?',
                type: 'SwitchField',
                default: false,
                getManagedDefault: ({
                    signupDetails,
                    defaults
                }) => UseFormUtils.applyBooleanDefault(signupDetails.onlineStudent, defaults.onlineStudent),
                visibleWhen: { permission: 'canEditOnline' }
            },
            {
                name: 'parent',
                label: 'Are you a parent?',
                type: 'SwitchField',
                default: false,
                getManagedDefault: ({
                    signupDetails,
                    defaults
                }) => UseFormUtils.applyBooleanDefault(signupDetails.parent, defaults.parent),
                visibleWhen: { permission: 'canEditParent' }
            },
            {
                name: 'fullTime',
                label: 'Are you a full-time student?',
                type: 'SwitchField',
                default: true,
                getManagedDefault: ({
                    signupDetails,
                    defaults
                }) => UseFormUtils.applyBooleanDefault(signupDetails.fullTime, defaults.fullTime),
                visibleWhen: { permission: 'canEditTimeStatus' }
            }
        ]
    },
    {
        name: 'demographics',
        visibleWhen: UseFormUtils.visibleWhenAnyChildVisible(),
        columns: 2,
        fields: [
            {
                name: 'pronouns',
                label: 'Pronouns',
                type: 'TextField',
                default: '',
                maxLength: MAX_PRONOUNS_LENGTH,
                visibleWhen: {
                    all: [
                        'signupOnly',
                        { prop: 'showPronounsTextField', value: true }
                    ]
                }
            },
            {
                name: 'ethnicity',
                label: 'Ethnicity',
                type: 'ChoiceWithSpecifyField',
                required: true,
                default: 'decline',
                getManagedDefault: ({
                    signupDetails,
                    defaults
                }) => signupDetails.ethnicity || defaults.ethnicity,
                options: UseFormUtils.buildChoiceOptions(RadioOptions.ethnicityRadioOptions),
                specifyLabel: 'Self describe',
                specifyField: {
                    name: 'ethnicityText',
                    label: 'Specify ethnicity',
                    type: 'TextField',
                    default: '',
                    refName: 'specifyEthnicityTextfield',
                    uiStateDefaults: {
                        ethnicityTextErrorShow: false
                    },
                    getManagedDefault: ({
                        signupDetails,
                        defaults
                    }) => RadioOptions.ethnicityRadioOptions.includes(signupDetails.ethnicity || defaults.ethnicity)
                        ? ''
                        : ((signupDetails.ethnicity || defaults.ethnicity) || defaults.ethnicityText),
                    maxLength: MAX_ETHNICITY_LENGTH
                },
                validateWith: [
                    {
                        type: 'profanity',
                        fieldName: 'specify'
                    },
                    'choiceWithSpecify'
                ],
                skipDefaultValidation: true,
                visibleWhen: {
                    all: [
                        'signupOnly',
                        { prop: 'rolePermissions.includeEthnicity', value: true }
                    ]
                }
            },
            {
                name: 'gender',
                label: 'Pronouns',
                type: 'ChoiceWithSpecifyField',
                required: true,
                default: 'decline',
                getManagedDefault: ({
                    signupDetails,
                    defaults
                }) => signupDetails.gender || defaults.gender,
                options: RadioOptions.pronounRadioOptions,
                specifyLabel: 'Self describe',
                specifyField: {
                    name: 'genderText',
                    label: 'Specify pronouns',
                    type: 'TextField',
                    default: '',
                    refName: 'specifyGenderTextfield',
                    uiStateDefaults: {
                        genderTextErrorShow: false
                    },
                    getManagedDefault: ({
                        signupDetails,
                        defaults
                    }) => FormConfigHelpers.getManagedPronounTextValue({
                        selectedPronoun: signupDetails.gender || defaults.gender,
                        fallbackText: defaults.genderText,
                        knownPronounValues: PRONOUN_OPTION_VALUES
                    }),
                    maxLength: MAX_PRONOUNS_LENGTH
                },
                validateWith: [
                    {
                        type: 'profanity',
                        fieldName: 'specify'
                    },
                    {
                        type: 'choiceWithSpecify',
                        permission: 'includePronouns',
                        specifyValidationLabel: 'Gender'
                    }
                ],
                skipDefaultValidation: true,
                visibleWhen: {
                    all: [
                        'signupOnly',
                        { prop: 'rolePermissions.includePronouns', value: true }
                    ]
                }
            },
            {
                name: 'housingText',
                label: 'Housing',
                type: 'TextField',
                default: '',
                getManagedDefault: ({
                    signupDetails,
                    defaults
                }) => signupDetails.housing?.name || defaults.housingText,
                render: false
            }
        ]
    },
        {
        name: 'parentStudent',
        visibleWhen: 'parentOnly',
        columns: 2,
        title: 'Your student',
        description: 'Tell us a little about the student you are supporting.',
        fields: [
            {
                name: 'studentName',
                label: 'Student name',
                type: 'TextField',
                validateWith: 'profanity',
                default: '',
                maxLength: MAX_FIRST_AND_LAST_NAME_LENGTH * 2,
                visibleWhen: 'parentOnly'
            },
            {
                name: 'studentBio',
                label: 'Student bio',
                type: 'TextareaField',
                validateWith: 'profanity',
                default: '',
                maxLength: MAX_BIO,
                visibleWhen: {
                    all: [
                        'parentOnly',
                        { permission: 'canEditStudentBio' }
                    ]
                }
            },
            {
                name: 'majorId',
                validateWith: {
                    type: 'namedSelection',
                    textFieldName: 'majorText',
                    selectedFieldName: 'majorId',
                    permission: 'canEditMajor',
                    message: 'Please choose a major in the list'
                },
                label: 'Major or Program',
                type: 'AutocompleteField',
                textFieldName: 'majorText',
                optionsSource: 'majors',
                buildSignupProgressPayload: ({ value }) => ({
                    majorId: value ? [value] : []
                }),
                visibleWhen: {
                    all: [
                        'parentOnly',
                        { permission: 'canEditMajor' }
                    ]
                }
            },
            internals.transferFieldConfig({
                visibleWhen: {
                    all: [
                        'parentOnly',
                        { permission: 'canEditTransfer' }
                    ]
                }
            }),
            {
                name: 'incomingClass',
                label: 'Incoming class',
                type: 'SelectField',
                optionsSource: 'incomingClassList',
                visibleWhen: {
                    all: [
                        'parentOnly',
                        { permission: 'canEditIncomingClass' }
                    ]
                }
            },
            {
                name: 'graduatingClass',
                label: 'Graduating class',
                type: 'SelectField',
                optionsSource: 'graduatingClassList',
                visibleWhen: {
                    all: [
                        'parentOnly',
                        { permission: 'canEditGraduatingClass' }
                    ]
                }
            },
            {
                name: 'onlineStudent',
                label: 'Are you an online student?',
                type: 'SwitchField',
                visibleWhen: {
                    all: [
                        'parentOnly',
                        { permission: 'canEditOnline' }
                    ]
                }
            }
        ]
    },
    {
        name: 'interests',
        visibleWhen: 'signupOnly',
        columns: 1,
        fields: [
            {
                name: 'interests',
                label: 'Interests',
                type: 'InterestsInputField',
                default: [],
                errorShowDefault: false,
                validateWith: {
                    type: 'interests',
                    requiredMessage: 'Start typing to find and select at least one Interest'
                },
                skipDefaultValidation: true,
                getManagedDefault: ({
                    signupDetails,
                    defaults,
                    props
                }) => (signupDetails.interests && signupDetails.interests.length > 0)
                    ? signupDetails.interests.slice()
                    : (props.defaultInterestId ? [props.defaultInterestId] : defaults.interests.slice()),
                buildSignupProgressPayload: ({
                    field,
                    formMethods,
                    value
                }) => ({
                    interests: internals.normalizeInterestIds(value),
                    passionInterests: internals.normalizeInterestIds(
                        internals.getFormFieldValue({
                            formMethods,
                            fieldName: field.passionFieldName || 'passionInterests'
                        })
                    )
                }),
                passionFieldName: 'passionInterests'
            },
            {
                name: 'passionInterests',
                label: 'Top interests',
                default: [],
                getManagedDefault: ({
                    signupDetails,
                    defaults
                }) => (signupDetails.passionInterests || defaults.passionInterests).slice(),
                render: false
            }
        ]
    }]
}));
